/**
 * Marketplace SubCategory Form Page (Create/Edit)
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
  useGetMarketplaceSubCategoryQuery,
  useCreateMarketplaceSubCategoryMutation,
  useUpdateMarketplaceSubCategoryMutation,
  useGetMarketplaceCategoriesQuery,
} from '../../store/api';
import toast from 'react-hot-toast';

interface SubCategoryFormState {
  category: number | '';
  name: string;
  name_ar: string;
  description: string;
  description_ar: string;
  order: number;
  is_active: boolean;
}

export const SubCategoryFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: subCategoryData, isLoading: isLoadingSubCategory } =
    useGetMarketplaceSubCategoryQuery(Number(id!), { skip: !isEdit });
  const { data: categoriesData } = useGetMarketplaceCategoriesQuery({ page_size: 100 });

  const [createSubCategory, { isLoading: isCreating }] = useCreateMarketplaceSubCategoryMutation();
  const [updateSubCategory, { isLoading: isUpdating }] = useUpdateMarketplaceSubCategoryMutation();

  const [formData, setFormData] = useState<SubCategoryFormState>({
    category: '',
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    order: 1,
    is_active: true,
  });
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && subCategoryData?.data) {
      const s = subCategoryData.data;
      setFormData({
        category: s.category,
        name: s.name,
        name_ar: s.name_ar || '',
        description: s.description || '',
        description_ar: s.description_ar || '',
        order: s.order,
        is_active: s.is_active,
      });
      setIconPreview(s.icon || null);
    }
  }, [isEdit, subCategoryData]);

  const isLoading = isCreating || isUpdating;

  const handleChange = (
    field: keyof SubCategoryFormState,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (f: File) => void,
    setPreview: (s: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error(t('marketplaceCatalog.subcategories.selectCategoryError'));
      return;
    }
    try {
      const body = new FormData();
      body.append('category', formData.category.toString());
      body.append('name', formData.name);
      body.append('name_ar', formData.name_ar);
      body.append('description', formData.description);
      body.append('description_ar', formData.description_ar);
      body.append('order', formData.order.toString());
      body.append('is_active', formData.is_active.toString());
      if (iconFile) body.append('icon', iconFile);

      if (isEdit) {
        await updateSubCategory({ id: Number(id!), data: body }).unwrap();
        toast.success(t('marketplaceCatalog.subcategories.toast.updated'));
      } else {
        await createSubCategory(body).unwrap();
        toast.success(t('marketplaceCatalog.subcategories.toast.created'));
      }
      navigate('/marketplace/subcategories');
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          (isEdit
            ? t('marketplaceCatalog.subcategories.toast.updateError')
            : t('marketplaceCatalog.subcategories.toast.createError'))
      );
    }
  };

  if (isEdit && isLoadingSubCategory) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('marketplaceCatalog.subcategories.editTitle') : t('marketplaceCatalog.subcategories.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.subCategories'), path: '/marketplace/subcategories' },
          { label: isEdit ? t('common.edit') : t('common.create') },
        ]}
      />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/marketplace/subcategories')} sx={{ mb: 3 }}>
        {t('marketplaceCatalog.subcategories.back')}
      </Button>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Icon */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('marketplaceCatalog.subcategories.iconLabel')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Avatar src={iconPreview || undefined} variant="rounded" sx={{ width: 100, height: 100 }}>
                    <ImageIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                  <Button variant="outlined" component="label" disabled={isLoading}>
                    {iconPreview ? t('marketplaceCatalog.form.changeIcon') : t('marketplaceCatalog.form.uploadIcon')}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setIconFile, setIconPreview)} />
                  </Button>
                </Box>
                <ImageSizeHint size={IMAGE_SIZES.labCategoryIcon} />
              </Box>

              <FormControl fullWidth required>
                <InputLabel>{t('marketplaceCatalog.form.category')}</InputLabel>
                <Select
                  value={formData.category}
                  label={t('marketplaceCatalog.form.category')}
                  onChange={(e) => handleChange('category', Number(e.target.value))}
                  disabled={isLoading}
                >
                  {categoriesData?.results?.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('marketplaceCatalog.form.name')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                disabled={isLoading}
              />
              <TextField
                fullWidth
                label={t('marketplaceCatalog.form.nameAr')}
                value={formData.name_ar}
                onChange={(e) => handleChange('name_ar', e.target.value)}
                disabled={isLoading}
                inputProps={{ dir: 'rtl' }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('marketplaceCatalog.form.description')}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isLoading}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('marketplaceCatalog.form.descriptionAr')}
                value={formData.description_ar}
                onChange={(e) => handleChange('description_ar', e.target.value)}
                disabled={isLoading}
                inputProps={{ dir: 'rtl' }}
              />
              <TextField
                fullWidth
                type="number"
                label={t('marketplaceCatalog.form.order')}
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">#</InputAdornment>,
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={t('marketplaceCatalog.form.active')}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/marketplace/subcategories')} disabled={isLoading}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isLoading || !formData.name || !formData.category}
                >
                  {isLoading
                    ? t('marketplaceCatalog.form.saving')
                    : isEdit
                    ? t('marketplaceCatalog.subcategories.updateButton')
                    : t('marketplaceCatalog.subcategories.createButton')}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubCategoryFormPage;
